import { captureException } from "@sentry/react";
import { OfflineError } from "@sportactics/errors";
import type { JSX, PropsWithChildren } from "react";
import { lazy, Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { SWRConfig as BaseSWRConfig } from "swr";

import { ApplicationsEffects } from "~applications";

import { CookiesProvider } from "./adapters/cookies";
import { FirebaseProvider } from "./adapters/firebase";
import { LocalForageProvider } from "./adapters/local-forage";
// eslint-disable-next-line import/no-internal-modules
import { UiProvider } from "./adapters/ui/ui-provider";
import { WebShareProvider } from "./adapters/web-share";

export function App(): JSX.Element {
  return (
    <ErrorBoundary fallback={<FatalErrorMessage />}>
      <SWRConfig>
        <FirebaseProvider>
          <LocalForageProvider>
            <UiProvider>
              <CookiesProvider>
                <WebShareProvider>
                  <Suspense>
                    <Ui />
                  </Suspense>
                  <Suspense>
                    <ApplicationsEffects />
                  </Suspense>
                </WebShareProvider>
              </CookiesProvider>
            </UiProvider>
          </LocalForageProvider>
        </FirebaseProvider>
      </SWRConfig>
    </ErrorBoundary>
  );
}

function FatalErrorMessage(): JSX.Element {
  return (
    <p>
      Oops something went wrong. This site is still in development. Sorry for
      that :) PLease try reloading?
    </p>
  );
}

function SWRConfig({ children }: PropsWithChildren): JSX.Element {
  const onError = (error: unknown): void => {
    if (error instanceof OfflineError) return;
    captureException(error);
  };
  return <BaseSWRConfig value={{ onError }}>{children}</BaseSWRConfig>;
}

const Ui = lazy(() =>
  // eslint-disable-next-line import/no-internal-modules
  import("./adapters/ui/ui").then((module) => ({
    default: module.Ui,
  })),
);
